import { API_CONFIG, getApiUrl } from '@/config/apiConfig';

const STORAGE_KEY_PREFIX = 'kiosk_info_analytics_v1:';
/** Full last successful GET /api/kiosk/info JSON (for offline fallback). */
const STORAGE_KEY_RAW_PREFIX = 'kiosk_info_api_raw_v1:';
const FETCH_DEDUPE_WINDOW_MS = 10 * 1000;

let inFlightKioskInfoRequest: Promise<KioskInfoPayload | null> | null = null;
let lastSuccessAt = 0;
let lastSuccessHostname = '';

export type KioskInfoPayload = {
  type: string;
  label?: string;
  domain?: string;
};

function storageKey(hostname: string): string {
  return `${STORAGE_KEY_PREFIX}${hostname}`;
}

function rawStorageKey(hostname: string): string {
  return `${STORAGE_KEY_RAW_PREFIX}${hostname}`;
}

export function readKioskInfoFromStorage(hostname: string): KioskInfoPayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(hostname));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as KioskInfoPayload;
    if (parsed && typeof parsed.type === 'string') return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Last successful API response body (same shape as GET /api/kiosk/info).
 * Use when offline instead of calling the API again.
 */
export function readKioskInfoApiRawFromStorage(hostname: string): unknown | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(rawStorageKey(hostname));
    if (!raw) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

export function writeKioskInfoToStorage(hostname: string, data: KioskInfoPayload): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey(hostname), JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

function writeKioskInfoApiRawToStorage(hostname: string, json: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(rawStorageKey(hostname), JSON.stringify(json));
  } catch {
    /* quota / private mode */
  }
}

function parseKioskInfoPayload(json: unknown): KioskInfoPayload | null {
  const d = (json as { data?: { type?: unknown; label?: unknown; domain?: unknown } })?.data;
  if (!d || typeof d.type !== 'string') return null;
  return {
    type: d.type,
    label: typeof d.label === 'string' ? d.label : undefined,
    domain: typeof d.domain === 'string' ? d.domain : undefined,
  };
}

/**
 * GET /api/kiosk/info — on success persists full JSON + slim payload to localStorage.
 * On failure (offline / error), returns last cached payload for this hostname if any.
 */
export function fetchKioskInfo(): Promise<KioskInfoPayload | null> {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (!hostname) return Promise.resolve(null);

  // Fast path: if we just fetched successfully, prefer cached value to avoid burst reload calls.
  if (
    lastSuccessHostname === hostname &&
    Date.now() - lastSuccessAt < FETCH_DEDUPE_WINDOW_MS
  ) {
    const cached = readKioskInfoFromStorage(hostname);
    if (cached) {
      return Promise.resolve(cached);
    }
  }

  if (inFlightKioskInfoRequest) {
    return inFlightKioskInfoRequest;
  }

  inFlightKioskInfoRequest = fetch(getApiUrl(API_CONFIG.ENDPOINTS.KIOSK.INFO), { credentials: 'include' })
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => {
      if (json) {
        writeKioskInfoApiRawToStorage(hostname, json);
        const payload = parseKioskInfoPayload(json);
        if (payload) {
          writeKioskInfoToStorage(hostname, payload);
          lastSuccessAt = Date.now();
          lastSuccessHostname = hostname;
          return payload;
        }
      }
      return readKioskInfoFromStorage(hostname);
    })
    .catch(() => readKioskInfoFromStorage(hostname))
    .finally(() => {
      inFlightKioskInfoRequest = null;
    });

  return inFlightKioskInfoRequest;
}
